import type {
  Identified,
  SoftDeletable,
  VersionedEntity
} from "../../shared/kernel/entity.js";
import type { UtcDateTime } from "../../shared/kernel/date-time.js";
import type {
  ProfessionalId,
  UserId,
  CompanyId,
  ClientId
} from "../../shared/kernel/identifiers.js";

export enum ProfessionalStatus {
  Active = "ACTIVE",
  Inactive = "INACTIVE"
}

export enum ProfessionalType {
  Engineer = "ENGINEER",
  Inspector = "INSPECTOR",
  DocumentationTechnician = "DOCUMENTATION_TECHNICIAN",
  NdtInspector = "NDT_INSPECTOR",
  Supervisor = "SUPERVISOR",
  Coordinator = "COORDINATOR",
  Planner = "PLANNER",
  Quality = "QUALITY",
  Other = "OTHER"
}

export enum QualificationAuthority {
  Cft = "CFT",
  Crea = "CREA",
  EndIso9712 = "END_ISO_9712",
  Snqc = "SNQC",
  Sncc = "SNCC",
  Other = "OTHER"
}

export interface ProfessionalQualification {
  readonly authority: QualificationAuthority;
  readonly registrationNumber: string;
  readonly title: string;
  readonly validUntil: UtcDateTime | null;
}

export interface DigitalSignatureReference {
  readonly provider: string;
  readonly credentialReference: string;
}

// REGRA V4-B: PROFESSIONAL É GLOBAL. Cliente pertence à ATRIBUIÇÃO, não ao Professional.
// TenantOwned foi removido; clientId permanece apenas como campo LEGADO opcional
// para compatibilidade com dados existentes e migration 029.
export interface Professional
  extends Identified<ProfessionalId>, VersionedEntity, SoftDeletable {
  /** LEGADO: não usar para autorização, ownership ou listagem. */
  readonly clientId?: ClientId | null;
  readonly userId: UserId | null;
  readonly name: string;
  readonly email: string;
  readonly functionTitle: string;
  readonly companyId?: CompanyId | string | null;
  readonly company?: string | null;
  readonly professionalType?: ProfessionalType | string;
  /**
   * Para profissionais do tipo ENGINEER (PLH), indica o cliente específico
   * ao qual este profissional está vinculado como responsável técnico habilitado.
   * Obrigatório quando professionalType === 'ENGINEER'.
   */
  readonly plhClientId?: string | null;
  readonly cft: string | null;
  readonly crea: string | null;
  readonly endIso?: string | null;
  readonly endIso9712?: string | null;
  readonly snqc: string | null;
  readonly sncc: string | null;
  readonly qualifications: readonly ProfessionalQualification[];
  readonly digitalSignature: DigitalSignatureReference | null;
  readonly signatureImageId?: string | null;
  readonly status: ProfessionalStatus;
}

